import { Injectable, inject } from '@angular/core';
import {
  ConfirmationService,
  TableDataPayload,
  TableLazyLoad,
  ToastService,
} from '@web/shared/ui';
import { map, Observable, switchMap, tap } from 'rxjs';
import { CollectionRequestBuilder } from '@web/common';
import { WarehouseRestockCollectionItem } from '../models/warehouse-restock-collection-item';
import { WarehouseRestockCollectionFilter } from '../models/warehouse-restock-collection-filter';
import { WarehouseRestockApiService } from '../api/services/warehouse-restock-api.service';
import { blank, Guid, isNil, once, valueOrDefault } from '@web/utils';
import {
  WarehouseRestockFormData,
  WarehouseRestockPersonFormData,
  WarehouseRestockProductFormData,
} from '../models/warehouse-restock-form-data';
import { WarehouseRestockCreateRequest } from '../api/requests/warehouse-restock-create.request';
import { WarehouseRestockDetails } from '../models/warehouse-restock-details';
import { WarehouseRestockPersonRequest } from '../api/requests/warehouse-restock-person.request';
import { WarehouseRestockProductRequest } from '../api/requests/warehouse-restock-product.request';
import { WarehouseRestockUpdateRequest } from '../api/requests/warehouse-restock-update.request';
import { WarehouseFileRequest } from '../api/requests/warehouse-file.request';
import { WarehouseRestockProduct } from '../models/warehouse-restock-product';
import { multimediaFromResponse } from '@web/shared/module/multimedia';

@Injectable({
  providedIn: 'root',
})
export class WarehouseRestockDataSourceService {
  private readonly warehouseRestockApi = inject(WarehouseRestockApiService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly toast = inject(ToastService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<WarehouseRestockCollectionFilter>,
  ): Observable<TableDataPayload<WarehouseRestockCollectionItem>> {
    return this.warehouseRestockApi
      .fetchCollection(
        this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('number').build(),
              )
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('shippingNumber').build(),
              )
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('personDignity').build(),
              )
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('personEmail').build(),
              )
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('personPhone').build(),
              )
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('status').build(),
              )
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('type').build(),
              )
              .appendDate((builder) => builder.rangeFor('restockedAt').build())
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .buildAsArray(),
          )
          .build(),
      )
      .pipe(
        map(
          (response) =>
            new TableDataPayload<WarehouseRestockCollectionItem>(
              response.items.map(
                (item) =>
                  new WarehouseRestockCollectionItem({
                    canBeApproved: item.contextOperations.canBeApproved,
                    canBeEdited: item.contextOperations.canBeEdited,
                    canBeRejected: item.contextOperations.canBeRejected,
                    canBeViewed: item.contextOperations.canBeViewed,
                    createdAt: item.createdAt,
                    id: item.id,
                    number: item.number,
                    personDignity: item.person?.dignity,
                    personEmail: item.person?.email,
                    personPhone: item.person?.phone,
                    restockedAt: item.restockedAt,
                    shippingNumber: item.shippingNumber,
                    status: item.status,
                    type: item.type,
                    orderId: item.order?.id,
                    orderNumber: item.order?.number,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  /* prettier-ignore */
  public fetchDetailsFormById(id: Guid): Observable<WarehouseRestockFormData> {
    return this.warehouseRestockApi.fetchDetailsById(id).pipe(
      switchMap((response) => this.warehouseRestockApi
        .fetchProductCollection(
          id,
          this.collectionRequestBuilder.disablePagination().build(),
        )
        .pipe(
          map(
            ({ items }) =>
              new WarehouseRestockFormData({
                restockedAt: response.restockedAt,
                shippingNumber: response.shippingNumber,
                type: response.type,
                person: isNil(response.person) ? null : new WarehouseRestockPersonFormData({
                  dignity: response.person.dignity,
                  phone: response.person.phone,
                  email: response.person.email,
                }),
                products: items.map(
                  (item) =>
                    new WarehouseRestockProductFormData({
                      id: item.id,
                      productId: item.good.id,
                      quantity: item.quantity,
                    }),
                ),
                comment: response.comment,
                fileIds: response.files.map((file) => file.id),
              }),
          ),
        )),
    );
  }

  public fetchDetailsById(id: Guid): Observable<WarehouseRestockDetails> {
    return this.warehouseRestockApi.fetchDetailsById(id).pipe(
      switchMap((response) =>
        this.warehouseRestockApi
          .fetchProductCollection(
            id,
            this.collectionRequestBuilder.disablePagination().build(),
          )
          .pipe(
            map(
              ({ items }) =>
                new WarehouseRestockDetails({
                  canBeApproved: response.contextOperations.canBeApproved,
                  canBeEdited: response.contextOperations.canBeEdited,
                  canBeRejected: response.contextOperations.canBeRejected,
                  canBeViewed: response.contextOperations.canBeViewed,
                  comment: response.comment,
                  createdAt: response.createdAt,
                  id: response.id,
                  number: response.number,
                  personDignity: response.person?.dignity,
                  personEmail: response.person?.email,
                  personPhone: response.person?.phone,
                  restockedAt: response.restockedAt,
                  approvedAt: response.approvedAt,
                  rejectedAt: response.rejectedAt,
                  shippingNumber: response.shippingNumber,
                  status: response.status,
                  type: response.type,
                  files: response.files.map((file) =>
                    multimediaFromResponse(file),
                  ),
                  products: items.map(
                    (item) =>
                      new WarehouseRestockProduct({
                        productId: item.good.id,
                        productName: item.good.name,
                        createdAt: item.createdAt,
                        id: item.id,
                        quantity: item.quantity,
                      }),
                  ),
                  orderNumber: response.order?.number,
                  orderId: response.order?.id,
                }),
            ),
          ),
      ),
    );
  }

  public approveById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz zatwierdzić zwrot?')
          .accept(() => {
            this.warehouseRestockApi
              .approveById(id)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess('Zwrot został zatwierdzony pomyślnie.')
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public rejectById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz odrzucić zwrot?')
          .accept(() => {
            this.warehouseRestockApi
              .rejectById(id)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess('Zwrot został odrzucony pomyślnie.')
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public createByFormData(data: WarehouseRestockFormData): Observable<void> {
    return this.warehouseRestockApi
      .create(
        new WarehouseRestockCreateRequest({
          comment: data.comment,
          type: data.type,
          person: new WarehouseRestockPersonRequest({
            dignity: data.person?.dignity,
            email: data.person?.email,
            phone: data.person?.phone,
          }),
          shippingNumber: data.shippingNumber,
          restockedAt: data.restockedAt,
          products: data.products.map(
            (product) =>
              new WarehouseRestockProductRequest({
                id: product.productId,
                quantity: product.quantity,
              }),
          ),
          files: valueOrDefault(data.fileIds, []).map(
            (id) => new WarehouseFileRequest(id),
          ),
        }),
      )
      .pipe(
        tap(() => {
          this.toast.show((builder) =>
            builder.initSuccess('Zwrot został dodany pomyślnie.').build(),
          );
        }),
        blank(),
      );
  }

  public updateByFormData(
    id: Guid,
    data: WarehouseRestockFormData,
  ): Observable<void> {
    return this.warehouseRestockApi
      .update(
        id,
        new WarehouseRestockUpdateRequest({
          comment: data.comment,
          type: data.type,
          person: new WarehouseRestockPersonRequest({
            dignity: data.person?.dignity,
            email: data.person?.email,
            phone: data.person?.phone,
          }),
          shippingNumber: data.shippingNumber,
          restockedAt: data.restockedAt,
          products: data.products.map(
            (product) =>
              new WarehouseRestockProductRequest({
                id: product.productId,
                quantity: product.quantity,
              }),
          ),
          files: valueOrDefault(data.fileIds, []).map(
            (id) => new WarehouseFileRequest(id),
          ),
        }),
      )
      .pipe(
        tap(() => {
          this.toast.show((builder) =>
            builder
              .initSuccess('Zwrot został zaktualizowane pomyślnie.')
              .build(),
          );
        }),
        blank(),
      );
  }
}
